import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import VehicleCard from '../../inventory/components/VehicleCard';
import { useVehicles } from '../../../hooks/useVehicles';
import { serviceAreaCities, CityData } from '../../../mocks/serviceAreaCities';
import NearbyAreasSection from '../components/NearbyAreasSection';
import ReadyToShopCta from '../components/ReadyToShopCta';
import WhyChooseUs from '../components/WhyChooseUs';
import Breadcrumb from '../../../components/feature/Breadcrumb';
import ServiceAreaStickyBar from '../../../components/layout/ServiceAreaStickyBar';
import { createTrustedHTML } from '../../../lib/trustedTypes';
import { PHONE_TEL, MAPS_URL } from '../../../lib/businessInfo';
import { useSEOHead } from '../../../hooks/useSEOHead';

interface CityLandingPageProps {
  cityName: string;
  state: string;
  distance: string;
  population: string;
  heroImage: string;
  whyPeopleBuyHere: string;
  whyTheyDrive: string;
  dealerInsight?: string;
  faqs: Array<{
    question: string;
    answer: string;
  }>;
}

export function CityLandingPage({
  cityName,
  state,
  distance,
  population,
  heroImage,
  whyPeopleBuyHere,
  whyTheyDrive,
  dealerInsight,
  faqs,
}: CityLandingPageProps) {
  const { citySlug } = useParams<{ citySlug: string }>();
  const [cityData, setCityData] = useState<CityData | null>(null);
  const { vehicles, loading } = useVehicles();
  const [filteredVehicles, setFilteredVehicles] = useState<any[]>([]);

  useEffect(() => {
    const city = serviceAreaCities.find(c => c.slug === citySlug);
    if (city) {
      setCityData(city);
    }
  }, [citySlug]);

  // SEO Head Management
  useSEOHead({
    title: cityData?.seo.title || '',
    description: cityData?.seo.description || '',
    keywords: cityData?.seo.keywords || '',
    canonicalUrl: `https://rockhoundauto.com/service-areas/${citySlug}`,
    geoRegion: `US-${cityData?.state === 'Oregon' ? 'OR' : 'WA'}`,
    geoPlacename: cityData ? `${cityData.name}, ${cityData.state}` : '',
    geoPosition: cityData ? `${cityData.coordinates.lat};${cityData.coordinates.lng}` : '',
  });

  // Inject BreadcrumbList and AutoDealer JSON-LD schemas
  useEffect(() => {
    if (!cityData) return;

    const breadcrumbSchema = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "Home",
          "item": "https://rockhoundauto.com/"
        },
        {
          "@type": "ListItem",
          "position": 2,
          "name": "Service Areas",
          "item": "https://rockhoundauto.com/service-areas"
        },
        {
          "@type": "ListItem",
          "position": 3,
          "name": `${cityData.name}, ${cityData.state}`,
          "item": `https://rockhoundauto.com/service-areas/${citySlug}`
        }
      ]
    };

    const autoDealerSchema = {
      "@context": "https://schema.org",
      "@type": "AutoDealer",
      "name": `Rockhound Auto - ${cityData.name}`,
      "description": `Used car dealer serving ${cityData.name}, ${cityData.state}. Free delivery, transparent pricing, no dealership games.`,
      "areaServed": {
        "@type": "City",
        "name": cityData.name,
        "containedInPlace": {
          "@type": "State",
          "name": cityData.state
        }
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": cityData.coordinates.lat,
        "longitude": cityData.coordinates.lng
      },
      "priceRange": "$$",
      "telephone": PHONE_TEL,
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "1425 SE 9th St",
        "addressLocality": "Warrenton",
        "addressRegion": "OR",
        "postalCode": "97146",
        "addressCountry": "US"
      }
    };

    const breadcrumbScriptId = `breadcrumb-schema-${citySlug}`;
    const autoDealerScriptId = `autodealer-schema-${citySlug}`;

    // Inject BreadcrumbList schema
    const breadcrumbScript = document.createElement('script');
    breadcrumbScript.id = breadcrumbScriptId;
    breadcrumbScript.type = 'application/ld+json';
    breadcrumbScript.textContent = JSON.stringify(breadcrumbSchema);
    document.head.appendChild(breadcrumbScript);

    // Inject AutoDealer schema
    const autoDealerScript = document.createElement('script');
    autoDealerScript.id = autoDealerScriptId;
    autoDealerScript.type = 'application/ld+json';
    autoDealerScript.textContent = JSON.stringify(autoDealerSchema);
    document.head.appendChild(autoDealerScript);

    // Cleanup on unmount
    return () => {
      const breadcrumbEl = document.getElementById(breadcrumbScriptId);
      const autoDealerEl = document.getElementById(autoDealerScriptId);
      if (breadcrumbEl) breadcrumbEl.remove();
      if (autoDealerEl) autoDealerEl.remove();
    };
  }, [cityData, citySlug]);

  useEffect(() => {
    if (vehicles.length > 0 && cityData) {
      const filtered = vehicles
        .filter(v => v.status === 'available')
        .slice(0, 12);
      setFilteredVehicles(filtered);
    }
  }, [vehicles, cityData]);

  if (!cityData) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">City Not Found</h1>
          <Link to="/service-areas" className="text-[#1A2332] hover:text-[#2C3E55]">
            View All Service Areas
          </Link>
        </div>
      </div>
    );
  }

  const { name, state: cityState, county, driveTime, population: cityPopulation, topVehicleTypes, localInsights, seo, coordinates, neighborhoods, whatBuyersWant, vehicleRecommendations, localCompetitorComparison } = cityData;

  return (
    <>
      {/* Breadcrumb Navigation */}
      <Breadcrumb
        items={[
          { label: 'Home', path: '/' },
          { label: 'Service Areas', path: '/service-areas' },
          { label: `${name}, ${state}`, path: `/service-areas/${citySlug}` },
        ]}
      />

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#F7F5F0] via-white to-amber-50 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Used Cars {name} {state === 'Oregon' ? 'OR' : 'WA'} | Free Delivery
            </h1>
            <p className="text-xl text-gray-700 mb-8">
              Free delivery to {name} • {driveTime} from our Warrenton location • No dealership games, just straight answers
            </p>
            <div className="flex flex-wrap justify-center gap-4 mb-8">
              <div className="bg-white px-6 py-3 rounded-lg shadow-sm border border-gray-200">
                <div className="text-sm text-gray-600">Population</div>
                <div className="text-lg font-semibold text-gray-900">{population}</div>
              </div>
              <div className="bg-white px-6 py-3 rounded-lg shadow-sm border border-gray-200">
                <div className="text-sm text-gray-600">Drive Time</div>
                <div className="text-lg font-semibold text-gray-900">{driveTime}</div>
              </div>
              <div className="bg-white px-6 py-3 rounded-lg shadow-sm border border-gray-200">
                <div className="text-sm text-gray-600">County</div>
                <div className="text-lg font-semibold text-gray-900">{county}</div>
              </div>
            </div>
            <div className="flex flex-wrap justify-center gap-4">
              <Link
                to="/inventory"
                className="bg-[#1A2332] text-white px-8 py-3 rounded-lg font-semibold hover:bg-[#2C3E55] transition whitespace-nowrap"
              >
                View Available Inventory
              </Link>
              <a
                href={MAPS_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-[#E8B84B] text-[#1A2332] px-8 py-3 rounded-lg font-semibold hover:bg-[#d4a43a] transition whitespace-nowrap flex items-center gap-2 cursor-pointer"
              >
                <i className="ri-map-pin-2-line"></i>
                Get Directions
              </a>
              <Link
                to="/contact"
                className="bg-white text-gray-900 px-8 py-3 rounded-lg font-semibold border-2 border-gray-300 hover:border-gray-400 transition whitespace-nowrap"
              >
                Schedule Delivery
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* What [City] Buyers Want */}
      {whatBuyersWant && (
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-6 text-center">
              What {name} Buyers Want in a Vehicle
            </h2>
            <div className="max-w-4xl mx-auto">
              <p className="text-lg text-gray-700 leading-relaxed mb-8">
                {whatBuyersWant}
              </p>
              {neighborhoods && neighborhoods.length > 0 && (
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">
                    Neighborhoods We Serve in {name}
                  </h3>
                  <div className="flex flex-wrap gap-3">
                    {neighborhoods.map((neighborhood, index) => (
                      <span
                        key={index}
                        className="bg-white px-4 py-2 rounded-lg text-gray-700 border border-gray-200"
                      >
                        {neighborhood}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Vehicle Recommendations */}
      {vehicleRecommendations && vehicleRecommendations.length > 0 && (
        <section className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">
              Best Vehicles for {name} Residents
            </h2>
            <div className="grid md:grid-cols-3 gap-8">
              {vehicleRecommendations.map((rec, index) => (
                <div key={index} className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                  <div className="w-12 h-12 bg-[#1A2332]/10 rounded-lg flex items-center justify-center mb-4">
                    <i className="ri-car-line text-2xl text-[#1A2332]"></i>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">{rec.type}</h3>
                  <p className="text-gray-700 mb-4">{rec.reason}</p>
                  <div className="space-y-2">
                    {rec.examples.map((example, idx) => (
                      <div key={idx} className="flex items-center text-sm text-gray-700">
                        <i className="ri-check-line text-[#1A2332] mr-2"></i>
                        {example}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Local Market Insights */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            Understanding the {name} Auto Market
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-gray-50 p-6 rounded-lg">
              <div className="w-12 h-12 bg-[#1A2332]/10 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-map-pin-line text-2xl text-[#1A2332]"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Local Lifestyle</h3>
              <p className="text-gray-700">{localInsights.lifestyle}</p>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg">
              <div className="w-12 h-12 bg-[#E8B84B]/10 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-car-line text-2xl text-[#E8B84B]"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Popular Vehicles</h3>
              <p className="text-gray-700">{localInsights.popularVehicles}</p>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg">
              <div className="w-12 h-12 bg-[#1A2332]/10 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-cloud-line text-2xl text-[#1A2332]"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Weather Considerations</h3>
              <p className="text-gray-700">{localInsights.weatherConsiderations}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Available Inventory */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Used Cars Available Near {name}
            </h2>
            <p className="text-lg text-gray-700 max-w-2xl mx-auto">
              All vehicles are in stock at our Warrenton location and available for free delivery to {name}.
              What you see is what you get — no bait and switch.
            </p>
          </div>

          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 animate-pulse">
                  <div className="bg-gray-200 h-48 rounded-lg mb-4"></div>
                  <div className="bg-gray-200 h-6 rounded mb-2"></div>
                  <div className="bg-gray-200 h-4 rounded w-2/3"></div>
                </div>
              ))}
            </div>
          ) : filteredVehicles.length > 0 ? (
            <>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
                {filteredVehicles.map((vehicle) => (
                  <VehicleCard key={vehicle.id} vehicle={vehicle} />
                ))}
              </div>
              <div className="text-center">
                <Link
                  to="/inventory"
                  className="inline-flex items-center text-[#1A2332] hover:text-[#2C3E55] font-semibold"
                >
                  View All Available Vehicles
                  <i className="ri-arrow-right-line ml-2"></i>
                </Link>
              </div>
            </>
          ) : (
            <div className="text-center py-12">
              <p className="text-gray-700 mb-4">No vehicles currently available. Check back soon!</p>
              <Link to="/inventory" className="text-[#1A2332] hover:text-[#2C3E55] font-semibold">
                View All Inventory
              </Link>
            </div>
          )}
        </div>
      </section>

      {/* Competitor Comparison */}
      {localCompetitorComparison && localCompetitorComparison.length > 0 && (
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-4 text-center">
              Why {name} Buyers Choose Rockhound Over Local Dealers
            </h2>
            <p className="text-lg text-gray-700 text-center max-w-3xl mx-auto mb-12">
              We&apos;re not here to bash other dealers—but {name} buyers deserve to know the real differences. Here&apos;s what sets us apart from traditional dealerships in the area.
            </p>
            <div className="space-y-6">
              {localCompetitorComparison.map((comp, index) => (
                <div key={index} className="bg-gray-50 p-6 rounded-lg border border-gray-200">
                  <div className="flex items-start">
                    <div className="flex-shrink-0 w-12 h-12 bg-[#E8B84B]/10 rounded-lg flex items-center justify-center mr-4">
                      <i className="ri-shield-check-line text-2xl text-[#E8B84B]"></i>
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-semibold text-gray-900 mb-2">
                        vs. {comp.dealerName}
                      </h3>
                      <p className="text-gray-700 leading-relaxed">
                        {comp.ourAdvantage}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-8 text-center">
              <Link
                to="/compare"
                className="inline-flex items-center text-[#1A2332] hover:text-[#2C3E55] font-semibold"
              >
                See Full Dealer Comparison
                <i className="ri-arrow-right-line ml-2"></i>
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Popular Vehicle Types for This Area */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            Shop by Vehicle Type in {name}
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {topVehicleTypes.map((type, index) => (
              <Link
                key={index}
                to={`/inventory?type=${type.toLowerCase()}`}
                className="bg-white p-6 rounded-lg text-center hover:shadow-md transition group border border-gray-200"
              >
                <div className="w-16 h-16 bg-[#1A2332]/10 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-[#1A2332]/20 transition">
                  <i className="ri-car-line text-3xl text-[#1A2332]"></i>
                </div>
                <h3 className="text-lg font-semibold text-gray-900">{type}</h3>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Rockhound for [City] */}
      <WhyChooseUs cityName={name} />

      {/* Service Area Map */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            Free Delivery to {name}
          </h2>
          <div className="bg-gray-100 rounded-lg overflow-hidden" style={{ height: '400px' }}>
            <iframe
              src={`https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d44697.89!2d${coordinates.lng}!3d${coordinates.lat}!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2z${encodeURIComponent(name + ', ' + state)}!5e0!3m2!1sen!2sus!4v1700000000006!5m2!1sen!2sus`}
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title={`Map of ${name}, ${state}`}
            ></iframe>
          </div>
          <div className="mt-6 text-center">
            <p className="text-gray-700 mb-4">
              <strong>Drive Time:</strong> {driveTime} from our Warrenton location
              <br />
              <strong>Delivery:</strong> Free to your home or workplace in {name}
            </p>
            <a
              href={MAPS_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-[#1A2332] text-white px-6 py-3 rounded-lg font-semibold hover:bg-[#2C3E55] transition cursor-pointer whitespace-nowrap"
            >
              <i className="ri-map-pin-2-line"></i>
              Get Directions to Our Lot
            </a>
          </div>
        </div>
      </section>

      {/* Nearby Service Areas */}
      <NearbyAreasSection currentCity={citySlug || ''} />

      {/* Final CTA */}
      <ReadyToShopCta cityName={name} />

      {/* Service Area Mobile Sticky Bar */}
      <ServiceAreaStickyBar />
    </>
  );
}